import { create } from 'zustand';
import type { QueueGroup } from '../api/reconciliation';

interface ReconciliationStore {
  // Selection (multi-select for bulk actions)
  selectedMatchIds: Set<string>;
  toggleSelected: (matchId: string) => void;
  clearSelection: () => void;
  selectAll: (matchIds: string[]) => void;
  deselectAll: (matchIds: string[]) => void;
  isSelected: (matchId: string) => boolean;

  // Drawer
  drawerMatchId: string | null;
  openDrawer: (matchId: string) => void;
  closeDrawer: () => void;

  // Filter chip
  activeFilter: QueueGroup;
  setFilter: (filter: QueueGroup) => void;

  // Dialogs
  manualMatchOpen: boolean;
  setManualMatchOpen: (open: boolean) => void;
  // When user picks "always do this for X" we want to pre-populate
  createRuleOpen: boolean;
  createRulePrefill: {
    merchantId?: string;
    patternText?: string;
    categoryId?: string;
  } | null;
  openCreateRule: (prefill?: ReconciliationStore['createRulePrefill']) => void;
  closeCreateRule: () => void;
}

export const useReconciliationStore = create<ReconciliationStore>((set, get) => ({
  selectedMatchIds: new Set(),
  toggleSelected: (matchId) =>
    set((state) => {
      const next = new Set(state.selectedMatchIds);
      if (next.has(matchId)) next.delete(matchId);
      else next.add(matchId);
      return { selectedMatchIds: next };
    }),
  clearSelection: () => set({ selectedMatchIds: new Set() }),
  selectAll: (matchIds) =>
    set((s) => ({
      selectedMatchIds: new Set([...s.selectedMatchIds, ...matchIds]),
    })),
  deselectAll: (matchIds) =>
    set((s) => {
      const next = new Set(s.selectedMatchIds);
      for (const id of matchIds) next.delete(id);
      return { selectedMatchIds: next };
    }),
  isSelected: (matchId) => get().selectedMatchIds.has(matchId),

  drawerMatchId: null,
  openDrawer: (matchId) => set({ drawerMatchId: matchId }),
  closeDrawer: () => set({ drawerMatchId: null }),

  activeFilter: 'all',
  setFilter: (filter) => set({ activeFilter: filter }),

  manualMatchOpen: false,
  setManualMatchOpen: (open) => set({ manualMatchOpen: open }),

  createRuleOpen: false,
  createRulePrefill: null,
  openCreateRule: (prefill) => set({ createRuleOpen: true, createRulePrefill: prefill ?? null }),
  closeCreateRule: () => set({ createRuleOpen: false, createRulePrefill: null }),
}));
